import React, { useEffect, useMemo, useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Input } from 'src/components/Input';
import { Select } from 'src/components/Select';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { AdminRoleRecord, PermissionCatalogItem, ROLE_STATUSES } from 'src/utils/rbacCatalog';
import {
  getRbacDescriptionError,
  getRbacDisplayNameError,
  isValidRbacDescription,
  isValidRbacDisplayName,
  sanitizeRbacLetterFieldInput,
} from 'src/utils/rbacValidation';
import { FieldError, FormField, FormGrid, PermissionCard, PermissionGrid } from '../styled';

export type RoleSavePayload = {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  status: string;
};

type RoleFormModalProps = {
  role: AdminRoleRecord | null;
  permissionCatalog: PermissionCatalogItem[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars -- callback arg name documents the save payload shape
  onSave: (payload: RoleSavePayload) => Promise<void>;
};

const permissionsEqual = (a: string[], b: string[]) =>
  [...a].sort().join('\0') === [...b].sort().join('\0');

const RoleFormModal: React.FC<RoleFormModalProps> = ({ role, permissionCatalog, onClose, onSave }) => {
  const isEdit = role !== null;
  const [key, setKey] = useState(role?.key ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [status, setStatus] = useState<string>(role?.status ?? 'active');
  const [permissions, setPermissions] = useState<string[]>(role?.permissions ?? []);
  const [initialName, setInitialName] = useState(role?.name ?? '');
  const [initialDescription, setInitialDescription] = useState(role?.description ?? '');
  const [initialStatus, setInitialStatus] = useState(role?.status ?? 'active');
  const [initialPermissions, setInitialPermissions] = useState<string[]>(role?.permissions ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const nextKey = role?.key ?? '';
    const nextName = role?.name ?? '';
    const nextDescription = role?.description ?? '';
    const nextStatus = role?.status ?? 'active';
    const nextPermissions = role?.permissions ?? [];
    setKey(nextKey);
    setName(nextName);
    setDescription(nextDescription);
    setStatus(nextStatus);
    setPermissions(nextPermissions);
    setInitialName(nextName);
    setInitialDescription(nextDescription);
    setInitialStatus(nextStatus);
    setInitialPermissions(nextPermissions);
  }, [role]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const nameError = getRbacDisplayNameError(name);
  const descriptionError = getRbacDescriptionError(description);
  const nameInvalid = trimmedName.length > 0 && nameError !== null;
  const descriptionInvalid = trimmedDescription.length > 0 && descriptionError !== null;
  const nameValid = isValidRbacDisplayName(name);
  const descriptionValid = isValidRbacDescription(description);
  const hasChanges =
    name !== initialName ||
    description !== initialDescription ||
    status !== initialStatus ||
    !permissionsEqual(permissions, initialPermissions);

  const canSave = useMemo(
    () =>
      isEdit
        ? Boolean(key.trim() && nameValid && descriptionValid && hasChanges)
        : Boolean(key.trim() && nameValid && descriptionValid),
    [isEdit, key, nameValid, descriptionValid, hasChanges],
  );

  const togglePermission = (permKey: string) => {
    if (permKey === 'admin:all') {
      setPermissions((prev) => (prev.includes(permKey) ? [] : [permKey]));
      return;
    }

    setPermissions((prev) => {
      const withoutAll = prev.filter((p) => p !== 'admin:all');
      return withoutAll.includes(permKey)
        ? withoutAll.filter((p) => p !== permKey)
        : [...withoutAll, permKey];
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave({
        key: key.trim(),
        name: trimmedName,
        description: trimmedDescription,
        permissions,
        status,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const actionButtons = (
    <Container display="flex" gap="0.75rem" justifyContent="flex-end" width="100%">
      <Button text="Cancel" className="outline" onClick={onClose} />
      <Button
        text={saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create role'}
        className="black"
        disabled={!canSave || saving}
        onClick={handleSubmit}
      />
    </Container>
  );

  return (
    <Modal isOpen onClose={onClose} footer={actionButtons}>
      <Container fontWeight={600} fontSize="1.25rem" paddingBottom="1rem">
        {isEdit ? 'Edit role' : 'Create role'}
      </Container>
      <FormGrid>
        <Input
          label="Key"
          placeholder="e.g. transaction-management"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={isEdit}
        />
        <FormField>
          <Input
            label="Name"
            placeholder="Display name"
            value={name}
            onChange={(e) => setName(sanitizeRbacLetterFieldInput(e.target.value))}
          />
          {nameInvalid && nameError && <FieldError>{nameError}</FieldError>}
        </FormField>
        <FormField>
          <Input
            label="Description"
            placeholder="What this role can do"
            value={description}
            onChange={(e) => setDescription(sanitizeRbacLetterFieldInput(e.target.value))}
          />
          {descriptionInvalid && descriptionError && <FieldError>{descriptionError}</FieldError>}
        </FormField>
        <Select
          label="Status"
          value={status}
          options={ROLE_STATUSES.map((s) => ({ value: s, label: s }))}
          onChange={(e) => setStatus(e.target.value)}
        />
        <Container fontWeight={600} fontSize="0.875rem">
          Permissions
        </Container>
        <PermissionGrid>
          {permissionCatalog.map((perm) => (
            <PermissionCard key={perm.key} $checked={permissions.includes(perm.key)}>
              <input
                type="checkbox"
                checked={permissions.includes(perm.key)}
                onChange={() => togglePermission(perm.key)}
              />
              <Container>
                <Container fontWeight={600} fontSize="0.8125rem">
                  {perm.label}
                </Container>
                <Container fontSize="0.75rem">{perm.key}</Container>
              </Container>
            </PermissionCard>
          ))}
        </PermissionGrid>
      </FormGrid>
    </Modal>
  );
};

export default RoleFormModal;
